import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchSshKeys, addSshKey, removeSshKey } from '../../api/sshKeys';
import { usePermission } from '../../hooks/usePermission';
import { useConfirm } from '../../context/ConfirmDialogContext';
import { useSnackbar } from '../../context/SnackbarContext';

interface Props {
  username: string;
}

export function SshKeysSection({ username }: Props) {
  const [addOpen, setAddOpen] = useState(false);
  const [newKey, setNewKey] = useState('');
  const canManage = usePermission('users.manage');
  const confirm = useConfirm();
  const showSnackbar = useSnackbar();
  const qc = useQueryClient();

  const { data: keys, isLoading } = useQuery({
    queryKey: ['ssh-keys', username],
    queryFn: () => fetchSshKeys(username),
  });

  const addMutation = useMutation({
    mutationFn: () => addSshKey(username, newKey),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ssh-keys', username] });
      showSnackbar('SSH key added', 'success');
      setNewKey('');
      setAddOpen(false);
    },
    onError: () => showSnackbar('Failed to add SSH key', 'error'),
  });

  const removeMutation = useMutation({
    mutationFn: (keyId: string) => removeSshKey(username, keyId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ssh-keys', username] }),
    onError: () => showSnackbar('Failed to remove SSH key', 'error'),
  });

  async function handleRemove(keyId: string, comment: string) {
    const ok = await confirm(`Remove SSH key${comment ? ` "${comment}"` : ''}?`);
    if (!ok) return;
    await removeMutation.mutateAsync(keyId);
  }

  function truncate(s: string, n: number) {
    return s.length > n ? s.slice(0, n) + '…' : s;
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="subtitle2">SSH Keys</Typography>
        {canManage && (
          <Button size="small" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
            Add Key
          </Button>
        )}
      </Stack>

      {isLoading && <CircularProgress size={20} />}
      {keys && keys.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No SSH keys configured.
        </Typography>
      )}
      {keys && keys.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Comment</TableCell>
              <TableCell>Fingerprint</TableCell>
              {canManage && <TableCell />}
            </TableRow>
          </TableHead>
          <TableBody>
            {keys.map((k) => (
              <TableRow key={k.id}>
                <TableCell><Chip label={k.type} size="small" variant="outlined" /></TableCell>
                <TableCell><Typography variant="body2">{k.comment || '—'}</Typography></TableCell>
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {truncate(k.id, 12)}
                    </Typography>
                    <Tooltip title="Copy fingerprint">
                      <IconButton size="small" onClick={() => void navigator.clipboard.writeText(k.id)}>
                        <ContentCopyIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
                {canManage && (
                  <TableCell>
                    <IconButton size="small" color="error" onClick={() => handleRemove(k.id, k.comment)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add SSH Public Key</DialogTitle>
        <DialogContent>
          <TextField
            multiline
            rows={5}
            fullWidth
            label="SSH public key"
            placeholder="ssh-ed25519 AAAA... user@host"
            value={newKey}
            onChange={(e) => setNewKey(e.target.value)}
            sx={{ mt: 1, fontFamily: 'monospace' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!newKey.trim() || addMutation.isPending}
            onClick={() => addMutation.mutate()}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
